import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../../../config/firebase';
import { FileText, Plus, CheckCircle, XCircle, Clock, Package } from 'lucide-react';
import './PurchaseOrdersTab.css';

const PurchaseOrdersTab = ({ hotelId, token }) => {
  const effectiveToken = token || localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('token');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [poForm, setPoForm] = useState({
    vendor_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    notes: '',
    items: [{ item_id: '', quantity_ordered: '', unit_price: '' }]
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getAuthHeaders = async (forceRefresh = false) => {
    const currentUser = auth.currentUser;

    if (currentUser) {
      const idToken = await currentUser.getIdToken(forceRefresh);
      return { Authorization: `Bearer ${idToken}` };
    }

    const fallbackToken =
      token ||
      localStorage.getItem('authToken') ||
      sessionStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('token');

    if (!fallbackToken) {
      throw new Error('Authentication token unavailable');
    }

    const sanitized = String(fallbackToken).replace(/^Bearer\s+/i, '').replace(/^"+|"+$/g, '').trim();
    return { Authorization: `Bearer ${sanitized}` };
  };

  const requestWithAuth = async (requestFactory) => {
    try {
      const headers = await getAuthHeaders(false);
      return await requestFactory(headers);
    } catch (err) {
      if (err?.response?.status === 401 && auth.currentUser) {
        const headers = await getAuthHeaders(true);
        return requestFactory(headers);
      }
      throw err;
    }
  };

  useEffect(() => {
    if (hotelId) {
      fetchPurchaseOrders();
      fetchVendors();
      fetchItems();
    }
  }, [hotelId, effectiveToken]);

  const fetchPurchaseOrders = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/purchase-orders?hotel_id=${hotelId}`,
        { headers }
      ));
      const poData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setPurchaseOrders(poData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setError('Failed to load purchase orders');
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/vendors?hotel_id=${hotelId}`,
        { headers }
      ));
      const vendorsData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setVendors(vendorsData);
    } catch (err) {
      console.error('Error fetching vendors:', err);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/items?hotel_id=${hotelId}`,
        { headers }
      ));
      const itemsData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setItems(itemsData);
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  const handleApprove = async (poId) => {
    if (!window.confirm('Approve this purchase order?')) return;

    try {
      await requestWithAuth((headers) => axios.put(
        `${process.env.REACT_APP_API_URL}/api/inventory/purchase-orders/${poId}/approve?hotel_id=${hotelId}`,
        { hotel_id: hotelId },
        { headers }
      ));
      setSuccess('Purchase order approved');
      fetchPurchaseOrders();
    } catch (err) {
      console.error('Error approving PO:', err);
      setError(err.response?.data?.message || 'Failed to approve purchase order');
    }
  };

  const updatePoField = (field, value) => {
    setPoForm((prev) => ({ ...prev, [field]: value }));
  };

  const updatePoItem = (index, field, value) => {
    setPoForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addPoItem = () => {
    setPoForm((prev) => ({
      ...prev,
      items: [...prev.items, { item_id: '', quantity_ordered: '', unit_price: '' }]
    }));
  };

  const removePoItem = (index) => {
    setPoForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const resetPoForm = () => {
    setPoForm({
      vendor_id: '',
      order_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '',
      notes: '',
      items: [{ item_id: '', quantity_ordered: '', unit_price: '' }]
    });
  };

  const getPoTotal = () => {
    return poForm.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity_ordered) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validItems = poForm.items.filter(
      (item) => item.item_id && parseFloat(item.quantity_ordered) > 0 && parseFloat(item.unit_price) >= 0
    );

    if (!poForm.vendor_id) {
      setError('Please select a vendor');
      return;
    }

    if (!poForm.order_date) {
      setError('Please select an order date');
      return;
    }

    if (validItems.length === 0) {
      setError('Please add at least one valid line item');
      return;
    }

    try {
      const payload = {
        hotel_id: hotelId,
        vendor_id: parseInt(poForm.vendor_id, 10),
        order_date: new Date(poForm.order_date).toISOString(),
        expected_delivery_date: poForm.expected_delivery_date
          ? new Date(poForm.expected_delivery_date).toISOString()
          : null,
        notes: poForm.notes || null,
        items: validItems.map((item) => ({
          item_id: parseInt(item.item_id, 10),
          quantity_ordered: parseFloat(item.quantity_ordered),
          unit_price: parseFloat(item.unit_price)
        }))
      };

      await requestWithAuth((headers) => axios.post(
        `${process.env.REACT_APP_API_URL}/api/inventory/purchase-orders?hotel_id=${hotelId}`,
        payload,
        { headers }
      ));

      setSuccess('Purchase order created successfully');
      setShowForm(false);
      resetPoForm();
      fetchPurchaseOrders();
    } catch (err) {
      console.error('Error creating PO:', err);
      setError(err.response?.data?.message || 'Failed to create purchase order');
    }
  };

  const handleReceive = async (poId) => {
    if (!window.confirm('Mark this purchase order as received?')) return;

    try {
      await requestWithAuth((headers) => axios.put(
        `${process.env.REACT_APP_API_URL}/api/inventory/purchase-orders/${poId}/receive?hotel_id=${hotelId}`,
        { received_date: new Date().toISOString(), hotel_id: hotelId },
        { headers }
      ));
      setSuccess('Purchase order received');
      fetchPurchaseOrders();
    } catch (err) {
      console.error('Error receiving PO:', err);
      setError(err.response?.data?.message || 'Failed to receive purchase order');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { icon: Clock, text: 'Pending', class: 'status-warning' },
      'approved': { icon: CheckCircle, text: 'Approved', class: 'status-info' },
      'received': { icon: Package, text: 'Received', class: 'status-success' },
      'cancelled': { icon: XCircle, text: 'Cancelled', class: 'status-danger' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;

    return (
      <span className={`status-badge ${config.class}`}>
        <Icon size={14} />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="loading-spinner"></div>
        <p>Loading purchase orders...</p>
      </div>
    );
  }

  return (
    <div className="purchase-orders-tab">
      <div className="tab-header">
        <h2><FileText size={24} /> Purchase Orders</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={20} /> {showForm ? 'Cancel' : 'Create PO'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {showForm && (
        <div className="form-container">
          <h3>Create Purchase Order</h3>
          <form onSubmit={handleCreatePO}>
            <div className="form-row">
              <div className="form-group">
                <label>Vendor *</label>
                <select
                  value={poForm.vendor_id}
                  onChange={(e) => updatePoField('vendor_id', e.target.value)}
                  required
                >
                  <option value="">Select Vendor</option>
                  {(Array.isArray(vendors) ? vendors : []).map((vendor) => (
                    <option key={vendor.vendor_id} value={vendor.vendor_id}>
                      {vendor.vendor_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Order Date *</label>
                <input
                  type="date"
                  value={poForm.order_date}
                  onChange={(e) => updatePoField('order_date', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Expected Delivery</label>
                <input
                  type="date"
                  value={poForm.expected_delivery_date}
                  onChange={(e) => updatePoField('expected_delivery_date', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                rows={2}
                value={poForm.notes}
                onChange={(e) => updatePoField('notes', e.target.value)}
                placeholder="Optional notes for this purchase order"
              />
            </div>

            <div className="form-group">
              <label>Line Items *</label>
              {(poForm.items || []).map((lineItem, index) => (
                <div key={index} className="form-row" style={{ marginBottom: '8px' }}>
                  <div className="form-group">
                    <select
                      value={lineItem.item_id}
                      onChange={(e) => updatePoItem(index, 'item_id', e.target.value)}
                      required
                    >
                      <option value="">Select Item</option>
                      {(Array.isArray(items) ? items : []).map((item) => (
                        <option key={item.item_id} value={item.item_id}>
                          {item.item_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={lineItem.quantity_ordered}
                      onChange={(e) => updatePoItem(index, 'quantity_ordered', e.target.value)}
                      placeholder="Qty"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={lineItem.unit_price}
                      onChange={(e) => updatePoItem(index, 'unit_price', e.target.value)}
                      placeholder="Unit Price"
                      required
                    />
                  </div>

                  <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => removePoItem(index)}
                      disabled={(poForm.items || []).length === 1}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              <button type="button" className="btn-secondary" onClick={addPoItem}>
                Add Item
              </button>
            </div>

            <div className="form-group">
              <strong>Total: ${getPoTotal().toFixed(2)}</strong>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">Create PO</button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  resetPoForm();
                  setShowForm(false);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="po-table-container">
        <table className="po-table">
          <thead>
            <tr>
              <th>PO Number</th>
              <th>Vendor</th>
              <th>Order Date</th>
              <th>Expected Delivery</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(purchaseOrders) ? purchaseOrders : []).map((po) => (
              <tr key={po.po_id}>
                <td><strong>{po.po_number}</strong></td>
                <td>{po.vendor_name}</td>
                <td>{new Date(po.order_date).toLocaleDateString()}</td>
                <td>{po.expected_delivery_date ? new Date(po.expected_delivery_date).toLocaleDateString() : '-'}</td>
                <td><strong>${parseFloat(po.total_amount || 0).toFixed(2)}</strong></td>
                <td>{getStatusBadge(po.status)}</td>
                <td>
                  <div className="action-buttons">
                    {po.status === 'pending' && (
                      <button 
                        className="btn-sm btn-success" 
                        onClick={() => handleApprove(po.po_id)}
                        title="Approve"
                      >
                        <CheckCircle size={16} /> Approve
                      </button>
                    )}
                    {po.status === 'approved' && (
                      <button 
                        className="btn-sm btn-primary" 
                        onClick={() => handleReceive(po.po_id)}
                        title="Receive"
                      >
                        <Package size={16} /> Receive
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(Array.isArray(purchaseOrders) ? purchaseOrders : []).length === 0 && (
        <div className="empty-state">
          <FileText size={48} />
          <h3>No Purchase Orders</h3>
          <p>Create purchase orders to track procurement</p>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersTab;
